<template>
  <div v-if="page" class="pp-slug" :style="{ '--pp-color': companyColor }">
    <template v-if="!editing">
      <div class="pp-slug__display">
        <link-icon size="16" />
        <span class="pp-slug__prefix">{{ prefix }}</span>
        <strong class="pp-slug__value">{{ page.path }}</strong>
        <!-- Grátis: o endereço com o nome aparece na mesma linha, como o próximo passo. -->
        <button
          v-if="!isPro && page.suggested_slug"
          type="button"
          class="pp-slug__upgrade"
          :title="`No PRO, seu link fica recrutei.me/${page.suggested_slug}`"
          @click="onProSlug('address_lock')"
        >
          <arrow-right-icon size="14" />
          <pro-badge />
          <span class="pp-slug__upgrade-value">{{ page.suggested_slug }}</span>
        </button>
        <button
          v-else-if="!isPro"
          type="button"
          class="pp-slug__pro"
          title="Escolha um endereço com o seu nome"
          @click="onProSlug('address_button')"
        >
          <pro-badge label="Personalizar" />
        </button>
        <button
          v-else
          type="button"
          class="pp-slug__edit"
          :disabled="!page.can_change_slug"
          :title="
            page.can_change_slug
              ? 'Mudar o endereço'
              : 'Você pode trocar o endereço 1 vez a cada 30 dias.'
          "
          @click="startEdit"
        >
          <edit-2-icon size="14" />
        </button>
      </div>
    </template>

    <form v-else class="pp-slug__form" @submit.prevent="save">
      <div class="pp-slug__display pp-slug__display--editing">
        <span class="pp-slug__prefix">{{ prefix }}</span>
        <input
          ref="input"
          v-model="value"
          class="pp-slug__input"
          maxlength="40"
          autocomplete="off"
          spellcheck="false"
          @input="onInput"
          @keydown.esc.prevent="cancel"
        />
      </div>
      <button type="submit" class="pp-btn pp-btn--primary pp-btn--small" :disabled="!canSave">
        {{ saving ? "Salvando..." : "Salvar" }}
      </button>
      <button type="button" class="pp-btn pp-btn--small" @click="cancel">Cancelar</button>
      <p class="pp-slug__status" :class="`pp-slug__status--${status}`">{{ statusText }}</p>
    </form>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { LinkIcon, Edit2Icon, ArrowRightIcon } from "vue-feather-icons";
import ProBadge from "./ProBadge.vue";
import mixin from "./mixin";
import { SLUG_RE, displayUrl, slugify } from "./constants";
import service, { errorReason, errorStatus } from "@/services/publicProfile";

export default {
  name: "SlugField",
  components: { LinkIcon, Edit2Icon, ArrowRightIcon, ProBadge },
  mixins: [mixin],
  data: () => ({
    editing: false,
    value: "",
    status: "idle", // idle | checking | available | taken | reserved | invalid | error
    saving: false,
  }),
  computed: {
    ...mapGetters("publicProfile", ["page", "isPro"]),
    prefix() {
      const url = displayUrl(this.page.url);
      const index = url.lastIndexOf(this.page.path);
      return index > 0 ? url.slice(0, index) : "recrutei.me/";
    },
    canSave() {
      return !this.saving && this.status === "available";
    },
    statusText() {
      return {
        idle: "",
        checking: "Verificando...",
        available: "Disponível",
        taken: "Esse endereço já está em uso.",
        reserved: "Esse endereço é reservado. Tente outro.",
        invalid: "Use de 3 a 40 letras minúsculas, números ou hífen.",
        same: "Esse já é o seu endereço.",
        error: "Não deu para verificar agora.",
      }[this.status];
    },
  },
  beforeDestroy() {
    clearTimeout(this.timer);
  },
  methods: {
    onProSlug(button) {
      this.ppTrack("public_profile_pro_cta_clicked", { cta: "slug", button });
      this.$emit("paywall", "slug");
    },
    startEdit() {
      this.ppTrack("public_profile_slug_edit_started", { current: this.page.slug, can_change: !!this.page.can_change_slug });
      this.value = this.page.slug;
      this.status = "idle";
      this.editing = true;
      this.$nextTick(() => this.$refs.input && this.$refs.input.select());
    },
    cancel() {
      this.ppTrack("public_profile_slug_edit_canceled", { typed: this.value, status: this.status });
      clearTimeout(this.timer);
      this.editing = false;
    },
    onInput() {
      this.value = slugify(this.value);
      clearTimeout(this.timer);
      if (this.value === this.page.slug) {
        this.status = "same";
        return;
      }
      if (!SLUG_RE.test(this.value)) {
        this.status = "invalid";
        return;
      }
      this.status = "checking";
      const slug = this.value;
      this.timer = setTimeout(async () => {
        try {
          const result = await service.slugAvailability(slug);
          if (slug !== this.value) return;
          this.status = result.available ? "available" : result.reason || "taken";
          this.ppTrack("public_profile_slug_checked", { value: slug, status: this.status, origin: "slug_field" });
        } catch (e) {
          if (slug === this.value) this.status = "error";
        }
      }, 400);
    },
    async save() {
      if (!this.canSave) return;
      this.saving = true;
      const from = this.page.slug;
      const to = this.value;
      try {
        await this.$store.dispatch("publicProfile/changeSlug", to);
        this.editing = false;
        this.ppTrack("public_profile_slug_changed", { from, to }, { setPerson: true });
        this.ppNotify("Endereço atualizado.");
      } catch (error) {
        const status = errorStatus(error);
        this.ppTrack("public_profile_slug_change_failed", { from, to, status, reason: errorReason(error) });
        if (status === 402) {
          this.editing = false;
          this.$emit("paywall", "slug");
        } else if (status === 429) {
          this.ppError("Você já trocou o endereço nos últimos 30 dias.");
        } else if (status === 409) {
          this.status = "taken";
        } else if (status === 422 && errorReason(error) === "slug_taken") {
          // Alguém pegou o nome entre a checagem e o salvar.
          this.status = "taken";
        } else if (status === 422 && errorReason(error) === "slug_reserved") {
          this.status = "reserved";
        } else if (status === 422) {
          this.status = "invalid";
        } else {
          this.ppError();
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss">
.pp-slug {
  font-family: Roboto, sans-serif;

  &__display {
    display: flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    height: 36px;
    padding: 0 12px;
    border-radius: 8px;
    background: #f2f4f7;
    color: #696969;
    font-size: 14px;

    svg {
      flex-shrink: 0;
    }

    &--editing {
      flex: 1;
      min-width: 0;
      border: 0.5px solid var(--pp-color);
      background: #fff;
    }
  }

  &__upgrade {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    margin-left: 2px;
    padding: 3px 8px 3px 4px;
    border: none;
    border-radius: 6px;
    background: none;
    color: #6554c0;
    font-family: inherit;
    font-size: 14px;
    cursor: pointer;

    svg {
      color: #a8a8a8;
    }

    &:hover {
      background: #ebe8f8;
    }
  }

  &__upgrade-value {
    overflow: hidden;
    max-width: 180px;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__prefix {
    white-space: nowrap;
  }

  &__value {
    overflow: hidden;
    color: #333;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__pro,
  &__edit {
    display: flex;
    align-items: center;
    padding: 2px;
    border: none;
    background: none;
    color: #4f4f4f;
    cursor: pointer;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &__form {
    position: relative;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__input {
    flex: 1;
    min-width: 80px;
    border: none;
    outline: none;
    background: transparent;
    color: #333;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 500;
  }

  &__status {
    position: absolute;
    top: 100%;
    left: 12px;
    margin-top: 4px;
    font-size: 12px;
    white-space: nowrap;

    &--available {
      color: #309e3a;
    }

    &--taken,
    &--reserved,
    &--invalid,
    &--error {
      color: #de2348;
    }

    &--checking,
    &--same {
      color: #696969;
    }
  }
}
</style>
